import React, { useState, useEffect } from 'react';
import {
    CreditCard,
    Wallet,
    X,
    Plus,
    CheckCircle2,
    AlertCircle,
    Eye,
    EyeOff,
    Building2,
    Coins,
    Shield,
    Copy,
    Save,
    RotateCcw,
    TrendingUp,
    Users,
    DollarSign,
    ArrowRight,
    Edit3,
    Banknote,
    Wallet2
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { allRemoteSettingsSelect, homeScreenStatSelect, userSelect } from '../../../redux/selectors';
import WrapUpP2P from '../p2p/layouts/WrapUpP2p';
import { authActions } from '../../../redux/actions';
import notify from '../../../utility/NotificationSystem';

const PaymentMethod = () => {
    // Mock Redux data - replace with your actual selectors
    const site_settings = useSelector(allRemoteSettingsSelect);
    const dispatch = useDispatch()
    const userData = useSelector(userSelect)
    const finance = useSelector(homeScreenStatSelect)

    // State management
    const [securityObj, setSecurityObj] = useState({});
    const [loading, setLoading] = useState(false);
    const [walletAddresses, setWalletAddresses] = useState({});
    const [updatingPaymentDetail, setUpdatingPaymentDetail] = useState(false);
    const [activeTab, setActiveTab] = useState("overview");
    const [notification, setNotification] = useState(null);
    const [bank, setBank] = useState(userData?.bank ?? "");
    const [accountName, setAccountName] = useState(userData?.accountName ?? "");
    const [accountNumber, setAccountNumber] = useState(userData?.accountNumber ?? "");
    const [iban, setIban] = useState(userData?.iban ?? "");
    const [editMode, setEditMode] = useState({ banking: true, crypto: false });
    const [showSensitiveData, setShowSensitiveData] = useState(false);

    // Determine which payment methods to show based on autoConfirm
    const showCrypto = site_settings?.plans?.some(el => el.mother?.autoConfirm == 1);
    const showBank = site_settings?.plans?.some(el => el.mother?.autoConfirm == 0);

    // Set initial tab based on available payment methods
    // useEffect(() => {
    //     if (showCrypto && !showBank) {
    //         setActiveTab("crypto");
    //     } else if (showBank && !showCrypto) {
    //         setActiveTab("banking");
    //     }
    // }, [showCrypto, showBank]);

    useEffect(() => {
        const cryptoWallets = {};
        userData?.bankdetails?.forEach((item) => {
            cryptoWallets[item.name] = item.value;
        });
        setWalletAddresses(cryptoWallets);
    }, [userData]);

    const updateSpecificCrypto = (cryptoName, value) => {
        setWalletAddresses((prev) => ({
            ...prev,
            [cryptoName]: value,
        }));
    };

    const showNotification = (message, type) => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };


    const doAddressChange = (e) => {
        if (updatingPaymentDetail) return
        e.preventDefault()
        setLoading(true)
        setUpdatingPaymentDetail(true)

        const updateObject = { ...(securityObj ?? {}), ...walletAddresses }

        dispatch(
            authActions.onLogin(`account/account_change/1`, updateObject, (response) => {
                notify(response.msg, response.code)
                setUpdatingPaymentDetail(false)
                setLoading(false)
                if (response.code == 1) {
                    // showNotification("Wallet addresses updated successfully", "success")
                } else {
                    // showNotification("Failed to update wallet addresses", "error")
                }
            }),
        )
    }

    const doBankUpdate = (e) => {
        if (updatingPaymentDetail) return
        e.preventDefault()
        setLoading(true)
        setUpdatingPaymentDetail(true)

        const updateObject = {
            iban,
            bank,
            accountName,
            accountNumber,
            ...(securityObj ?? {}),
        }

        dispatch(
            authActions.onLogin(`account/account_change/0`, updateObject, (response) => {
                notify(response.msg, response.code)
                setLoading(false)
                setUpdatingPaymentDetail(false)
                if (response.code == 1) {
                    // showNotification("Bank details updated successfully", "success")
                } else {
                    // showNotification("Failed to update bank details", "error")
                }
            }),
        )
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showNotification("Address copied to clipboard", "success");
    };

    const hasBankDetails = userData?.bank;
    const hasWalletAddresses = userData?.bankdetails?.some((item) => item?.value);

    // If neither payment method should be shown, display a message
    if (!showCrypto && !showBank) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center space-y-6">
                    <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center">
                        <Building2 className="w-12 h-12 text-black" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white mb-2">Payment Methods Unavailable</h1>
                        <p className="text-gray-400">No payment methods are available for your selected currency.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (<WrapUpP2P allowAutoPadding={false}>
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-900 to-slate-900 text-white overflow-hidden">
            {/* Animated Background Grid */}
            <div className="fixed inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, rgb(250, 204, 21) 1px, transparent 0)`,
                    backgroundSize: '50px 50px'
                }}></div>
            </div>

            {/* Main Content */}
            <div className="relative z-10">
                {/* Header Terminal */}
                <div className="border-b border-yellow-500/20 bg-gradient-to-r from-gray-900/50 to-black/50 backdrop-blur-sm">
                    <div className="max-w-7xl mx-auto px-8 py-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-6">

                            </div>
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={() => setShowSensitiveData(!showSensitiveData)}
                                    className="flex items-center space-x-2 px-3 py-1 rounded-lg bg-yellow-500/10 border border-yellow-500/20 hover:bg-yellow-500/20 transition-all duration-300"
                                >
                                    {showSensitiveData ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    <span className="text-xs text-yellow-400 font-mono">
                                        {showSensitiveData ? 'HIDE_DATA' : 'SHOW_DATA'}
                                    </span>
                                </button>
                                <div className="flex items-center space-x-2 px-3 py-1 rounded-lg bg-green-500/10 border border-green-500/20">
                                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                                    <span className="text-xs text-green-400 font-mono">P2P_SECURE</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="border-b border-gray-800">
                    <div className="max-w-7xl mx-auto px-8">
                        <div className="flex space-x-1">
                            <button
                                onClick={() => setActiveTab('overview')}
                                className={`px-6 py-4 font-mono text-sm transition-all duration-300 border-b-2 ${activeTab === 'overview'
                                        ? 'border-yellow-500 text-yellow-400 bg-yellow-500/5'
                                        : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/30'
                                    }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <TrendingUp className="w-4 h-4" />
                                    <span>INVESTMENT_OVERVIEW</span>
                                </div>
                            </button>

                            {showBank && (
                                <button
                                    onClick={() => setActiveTab('banking')}
                                    className={`px-6 py-4 font-mono text-sm transition-all duration-300 border-b-2 ${activeTab === 'banking'
                                            ? 'border-yellow-500 text-yellow-400 bg-yellow-500/5'
                                            : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/30'
                                        }`}
                                >
                                    <div className="flex items-center space-x-2">
                                        <Building2 className="w-4 h-4" />
                                        <span>BANKING_SYSTEM</span>
                                    </div>
                                </button>
                            )}

                            {showCrypto && (
                                <button
                                    onClick={() => setActiveTab('crypto')}
                                    className={`px-6 py-4 font-mono text-sm transition-all duration-300 border-b-2 ${activeTab === 'crypto'
                                            ? 'border-yellow-500 text-yellow-400 bg-yellow-500/5'
                                            : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/30'
                                        }`}
                                >
                                    <div className="flex items-center space-x-2">
                                        <Coins className="w-4 h-4" />
                                        <span>CRYPTO_VAULT</span>
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="max-w-7xl mx-auto px-8 py-8">

                    {/* Overview Section */}
                    {activeTab === 'overview' && (
                        <div className="space-y-8">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h1 className="text-3xl font-bold text-white mb-2">P2P Investment Dashboard</h1>
                                    <p className="text-gray-400 font-mono">Monitor your payment methods and investment readiness</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
                                {/* Banking Overview */}
                                {showBank && (
                                    <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl border border-yellow-500/20 p-8">
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="flex items-center space-x-3">
                                                <Building2 className="w-6 h-6 text-yellow-400" />
                                                <h2 className="text-xl font-bold text-white">Banking Configuration</h2>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className={`w-2 h-2 rounded-full ${hasBankDetails ? 'bg-green-400' : 'bg-red-400'} animate-pulse`}></div>
                                                <span className={`text-sm font-mono ${hasBankDetails ? 'text-green-400' : 'text-red-400'}`}>
                                                    {hasBankDetails ? 'CONFIGURED' : 'PENDING'}
                                                </span>
                                            </div>
                                        </div>

                                        {hasBankDetails ? (
                                            <div className="space-y-4">
                                                <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <span className="text-gray-300 font-mono text-sm">Financial Institution</span>
                                                        <button
                                                            onClick={() => setActiveTab('banking')}
                                                            className="text-yellow-400 hover:text-yellow-300 transition-colors"
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <p className="text-white font-semibold">{userData.bank}</p>
                                                </div>

                                                <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
                                                    <span className="text-gray-300 font-mono text-sm block mb-2">Account Holder</span>
                                                    <p className="text-white font-semibold">{userData.accountName}</p>
                                                </div>

                                                <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
                                                    <span className="text-gray-300 font-mono text-sm block mb-2">Account Details</span>
                                                    <div className="flex items-center space-x-3">
                                                        <span className="px-3 py-1 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 font-mono text-sm">
                                                            {showSensitiveData ? userData.accountNumber : "****-****-****-9012"}
                                                        </span>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => setActiveTab('banking')}
                                                    className="w-full mt-4 p-3 rounded-xl bg-gradient-to-r from-yellow-400/10 to-amber-500/10 border border-yellow-500/20 hover:border-yellow-500/40 text-yellow-400 font-mono text-sm transition-all duration-300 flex items-center justify-center space-x-2"
                                                >
                                                    <span>MODIFY_BANKING_CONFIG</span>
                                                    <ArrowRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <Banknote className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                                <h3 className="text-white font-bold mb-2">No Banking Setup</h3>
                                                <p className="text-gray-400 text-sm mb-6">Configure your bank account to enable P2P investments</p>
                                                <button
                                                    onClick={() => setActiveTab('banking')}
                                                    className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold rounded-xl hover:from-yellow-300 hover:to-amber-400 transition-all duration-300"
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        <Plus className="w-5 h-5" />
                                                        <span>SETUP_BANKING</span>
                                                    </div>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Crypto Overview */}
                                {showCrypto && (
                                    <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl border border-yellow-500/20 p-8">
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="flex items-center space-x-3">
                                                <Coins className="w-6 h-6 text-yellow-400" />
                                                <h2 className="text-xl font-bold text-white">Crypto Wallet Status</h2>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className={`w-2 h-2 rounded-full ${hasWalletAddresses ? 'bg-green-400' : 'bg-red-400'} animate-pulse`}></div>
                                                <span className={`text-sm font-mono ${hasWalletAddresses ? 'text-green-400' : 'text-red-400'}`}>
                                                    {hasWalletAddresses ? 'ACTIVE' : 'INACTIVE'}
                                                </span>
                                            </div>
                                        </div>

                                        {hasWalletAddresses ? (
                                            <div className="space-y-3">
                                                {userData?.bankdetails?.map((item, index) => {
                                                    if (!item?.value) return null;
                                                    const image = finance?.find((finItem) => finItem.type === item.type)?.image;

                                                    return (
                                                        <div key={index} className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400/20 to-amber-600/20 flex items-center justify-center">
                                                                        {image ? (
                                                                            <img src={image} alt={item.name} className="w-5 h-5 rounded-full" />
                                                                        ) : (
                                                                            <Wallet className="w-4 h-4 text-yellow-400" />
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        <h3 className="text-white font-semibold text-sm">{item.name}</h3>
                                                                        <span className="text-yellow-400 font-mono text-xs">{item.abbr}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs font-mono">
                                                                        CONNECTED
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                <button
                                                    onClick={() => setActiveTab('crypto')}
                                                    className="w-full mt-4 p-3 rounded-xl bg-gradient-to-r from-yellow-400/10 to-amber-500/10 border border-yellow-500/20 hover:border-yellow-500/40 text-yellow-400 font-mono text-sm transition-all duration-300 flex items-center justify-center space-x-2"
                                                >
                                                    <span>MANAGE_CRYPTO_VAULT</span>
                                                    <ArrowRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                                <h3 className="text-white font-bold mb-2">No Crypto Wallets</h3>
                                                <p className="text-gray-400 text-sm mb-6">Add wallet addresses to enable crypto investments</p>
                                                <button
                                                    onClick={() => setActiveTab('crypto')}
                                                    className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold rounded-xl hover:from-yellow-300 hover:to-amber-400 transition-all duration-300"
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        <Plus className="w-5 h-5" />
                                                        <span>SETUP_CRYPTO</span>
                                                    </div>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Investment Stats */}
                            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl border border-yellow-500/20 p-8">
                                <div className="flex items-center space-x-3 mb-6">
                                    <Users className="w-6 h-6 text-yellow-400" />
                                    <h2 className="text-xl font-bold text-white">P2P Investment Readiness</h2>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 text-center">
                                        <Wallet2 className="w-8 h-8 text-green-400 mx-auto mb-3" />
                                        <h3 className="text-white font-bold">Payment Methods</h3>
                                        <p className="text-2xl font-bold text-green-400 mt-2">
                                            {(hasBankDetails ? 1 : 0) + (hasWalletAddresses ? 1 : 0)}/1
                                        </p>
                                        <p className="text-gray-400 text-sm mt-1">Configured</p>
                                    </div>

                                    <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 text-center">
                                        <Shield className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                                        <h3 className="text-white font-bold">Security Status</h3>
                                        <p className="text-2xl font-bold text-yellow-400 mt-2">HIGH</p>
                                        <p className="text-gray-400 text-sm mt-1">Encrypted</p>
                                    </div>

                                    <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 text-center">
                                        <TrendingUp className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                                        <h3 className="text-white font-bold">Investment Ready</h3>
                                        <p className="text-2xl font-bold text-blue-400 mt-2">
                                            {hasBankDetails || hasWalletAddresses ? 'YES' : 'NO'}
                                        </p>
                                        <p className="text-gray-400 text-sm mt-1">Status</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Banking Section */}
                    {activeTab === 'banking' && showBank && (
                        <div className="space-y-8">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h1 className="text-3xl font-bold text-white mb-2">Banking System Configuration</h1>
                                    <p className="text-gray-400 font-mono">Configure traditional banking details for P2P investments</p>
                                </div>
                                <div className="flex items-center space-x-4">
                                    {!editMode.banking ? (
                                        <button
                                            onClick={() => setEditMode(prev => ({ ...prev, banking: true }))}
                                            className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold rounded-xl hover:from-yellow-300 hover:to-amber-400 transition-all duration-300"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <Edit3 className="w-5 h-5" />
                                                <span>{hasBankDetails ? 'EDIT_CONFIG' : 'ADD_BANK'}</span>
                                            </div>
                                        </button>
                                    ) : (
                                        <div className="flex space-x-3">
                                            <button
                                                onClick={() => {
                                                    setBank(userData?.bank ?? "");
                                                    setAccountName(userData?.accountName ?? "");
                                                    setAccountNumber(userData?.accountNumber ?? "");
                                                    setIban(userData?.iban ?? "");
                                                    setEditMode(prev => ({ ...prev, banking: false }));
                                                }}
                                                className="px-4 py-3 bg-gray-800 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-all duration-300"
                                            >
                                                <RotateCcw className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={doBankUpdate}
                                                disabled={updatingPaymentDetail}
                                                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:from-green-400 hover:to-emerald-500 transition-all duration-300 disabled:opacity-50"
                                            >
                                                <div className="flex items-center space-x-2">
                                                    {updatingPaymentDetail ? (
                                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    ) : (
                                                        <Save className="w-5 h-5" />
                                                    )}
                                                    <span>{updatingPaymentDetail ? 'SAVING...' : 'SAVE_CONFIG'}</span>
                                                </div>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Banking Form */}
                            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl border border-yellow-500/20 overflow-hidden">
                                <div className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 p-6 border-b border-yellow-500/20">
                                    <div className="flex items-center space-x-3">
                                        <Building2 className="w-6 h-6 text-yellow-400" />
                                        <h2 className="text-xl font-bold text-white">Traditional Banking Module</h2>
                                    </div>
                                </div>

                                <div className="p-8 space-y-8">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-sm font-bold text-yellow-400 mb-3 font-mono uppercase tracking-wider">
                                                    Financial Institution
                                                </label>
                                                {editMode.banking ? (
                                                    <input
                                                        type="text"
                                                        value={bank}
                                                        onChange={(e) => setBank(e.target.value)}
                                                        className="w-full px-4 py-4 bg-black/50 border border-gray-700 rounded-xl focus:border-yellow-500 focus:outline-none text-white font-mono"
                                                        placeholder="Enter bank name..."
                                                    />
                                                ) : (
                                                    <div className="px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white font-mono">
                                                        {userData.bank || 'Not configured'}
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-bold text-yellow-400 mb-3 font-mono uppercase tracking-wider">
                                                    Account Holder
                                                </label>
                                                {editMode.banking ? (
                                                    <input
                                                        type="text"
                                                        value={accountName}
                                                        onChange={(e) => setAccountName(e.target.value)}
                                                        className="w-full px-4 py-4 bg-black/50 border border-gray-700 rounded-xl focus:border-yellow-500 focus:outline-none text-white font-mono"
                                                        placeholder="Enter account holder name..."
                                                    />
                                                ) : (
                                                    <div className="px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white font-mono">
                                                        {userData.accountName || 'Not configured'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-sm font-bold text-yellow-400 mb-3 font-mono uppercase tracking-wider">
                                                    Account Number
                                                </label>
                                                {editMode.banking ? (
                                                    <input
                                                        type="text"
                                                        value={accountNumber}
                                                        onChange={(e) => setAccountNumber(e.target.value)}
                                                        className="w-full px-4 py-4 bg-black/50 border border-gray-700 rounded-xl focus:border-yellow-500 focus:outline-none text-white font-mono"
                                                        placeholder="Enter account number..."
                                                    />
                                                ) : (
                                                    <div className="px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white font-mono">
                                                        {showSensitiveData ? (userData.accountNumber || 'Not configured') : '****-****-****-9012'}
                                                    </div>
                                                )}
                                            </div>

                                           
                                        </div>
                                    </div>

                                    {editMode.banking && (
                                        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
                                            <div className="flex items-start space-x-3">
                                                <AlertCircle className="w-6 h-6 text-red-400 mt-1" />
                                                <div>
                                                    <h3 className="text-red-400 font-bold mb-2">Security Notice</h3>
                                                    <p className="text-red-300/80 text-sm">
                                                        Banking information is encrypted and stored securely. Ensure all details are accurate before saving to enable P2P investment transactions.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Crypto Section */}
                    {activeTab === 'crypto' && showCrypto && (
                        <div className="space-y-8">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h1 className="text-3xl font-bold text-white mb-2">Crypto Vault Management</h1>
                                    <p className="text-gray-400 font-mono">Configure blockchain wallet addresses for digital investments</p>
                                </div>
                                <div className="flex items-center space-x-4">
                                    {!editMode.crypto ? (
                                        <button
                                            onClick={() => setEditMode(prev => ({ ...prev, crypto: true }))}
                                            className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold rounded-xl hover:from-yellow-300 hover:to-amber-400 transition-all duration-300"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <Edit3 className="w-5 h-5" />
                                                <span>{hasWalletAddresses ? 'UPDATE_VAULT' : 'ADD_WALLETS'}</span>
                                            </div>
                                        </button>
                                    ) : (
                                        <div className="flex space-x-3">
                                            <button
                                                onClick={() => {
                                                    const cryptoWallets = {};
                                                    userData?.bankdetails?.forEach((item) => {
                                                        cryptoWallets[item.name] = item.value;
                                                    });
                                                    setWalletAddresses(cryptoWallets);
                                                    setEditMode(prev => ({ ...prev, crypto: false }));
                                                }}
                                                className="px-4 py-3 bg-gray-800 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-all duration-300"
                                            >
                                                <RotateCcw className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={doAddressChange}
                                                disabled={updatingPaymentDetail}
                                                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:from-green-400 hover:to-emerald-500 transition-all duration-300 disabled:opacity-50"
                                            >
                                                <div className="flex items-center space-x-2">
                                                    {updatingPaymentDetail ? (
                                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    ) : (
                                                        <Save className="w-5 h-5" />
                                                    )}
                                                    <span>{updatingPaymentDetail ? 'UPDATING...' : 'SAVE_VAULT'}</span>
                                                </div>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Crypto Wallets */}
                            <div className="space-y-6">
                                {userData?.bankdetails?.map((item, index) => {
                                    const image = finance?.find((finItem) => finItem.type === item.type)?.image;

                                    return (
                                        <div key={index} className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl border border-yellow-500/20 overflow-hidden">
                                            <div className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 p-6 border-b border-yellow-500/20">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-4">
                                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400/20 to-amber-600/20 flex items-center justify-center">
                                                            {image ? (
                                                                <img src={image} alt={item.name} className="w-8 h-8 rounded-full" />
                                                            ) : (
                                                                <Wallet className="w-6 h-6 text-yellow-400" />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h3 className="text-xl font-bold text-white">{item.name} Wallet</h3>
                                                            <p className="text-yellow-400 font-mono text-sm">{item.abbr}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center space-x-3">
                                                        <div className={`px-3 py-1 rounded-lg text-xs font-mono ${item.value ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                                            }`}>
                                                            {item.value ? 'CONFIGURED' : 'PENDING'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-6">
                                                <div className="space-y-4">
                                                    <label className="block text-sm font-bold text-yellow-400 font-mono uppercase tracking-wider">
                                                        Wallet Address
                                                    </label>

                                                    {editMode.crypto ? (
                                                        <div className="flex space-x-3">
                                                            <input
                                                                type="text"
                                                                value={walletAddresses[item.name] || ''}
                                                                onChange={(e) => updateSpecificCrypto(item.name, e.target.value)}
                                                                className="flex-1 px-4 py-4 bg-black/50 border border-gray-700 rounded-xl focus:border-yellow-500 focus:outline-none text-white font-mono text-sm"
                                                                placeholder={`Enter ${item.name} wallet address...`}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center space-x-3">
                                                            <div className="flex-1 px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white font-mono text-sm break-all">
                                                                {item.value
                                                                    ? (showSensitiveData
                                                                        ? item.value
                                                                        : `${item.value.substring(0, 8)}...${item.value.substring(item.value.length - 8)}`)
                                                                    : 'Not configured'
                                                                }
                                                            </div>
                                                            {item.value && (
                                                                <button
                                                                    onClick={() => copyToClipboard(item.value)}
                                                                    className="px-4 py-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl hover:bg-yellow-500/20 transition-all duration-300"
                                                                >
                                                                    <Copy className="w-5 h-5 text-yellow-400" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {editMode.crypto && (
                                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-6">
                                    <div className="flex items-start space-x-3">
                                        <AlertCircle className="w-6 h-6 text-amber-400 mt-1" />
                                        <div>
                                            <h3 className="text-amber-400 font-bold mb-2">Critical Warning</h3>
                                            <p className="text-amber-300/80 text-sm">
                                                Double-check all wallet addresses before saving. Incorrect addresses will result in permanent loss of funds.
                                                Always verify addresses on multiple sources and ensure they match your actual wallet addresses.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Notification Toast */}
            {notification && (
                <div className="fixed bottom-4 right-4 z-50 flex items-center p-4 mb-4 w-full max-w-xs rounded-lg shadow bg-black/90 backdrop-blur-xl border border-yellow-500/30">
                    <div className={`inline-flex flex-shrink-0 justify-center items-center w-8 h-8 rounded-lg ${notification.type === "success" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                        }`}>
                        {notification.type === "success" ? (
                            <CheckCircle2 className="w-5 h-5" />
                        ) : (
                            <AlertCircle className="w-5 h-5" />
                        )}
                    </div>
                    <div className="ml-3 text-sm font-mono text-white">{notification.message}</div>
                    <button
                        type="button"
                        className="ml-auto -mx-1.5 -my-1.5 bg-transparent text-gray-400 hover:text-white rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-800 inline-flex h-8 w-8"
                        onClick={() => setNotification(null)}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Footer Terminal Info */}
            <div className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm border-t border-yellow-500/20 p-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center space-x-6 text-xs font-mono text-gray-400">
                        <span>P2P_STATUS: <span className="text-green-400">READY</span></span>
                        <span>PAYMENT_METHODS: <span className="text-yellow-400">{(hasBankDetails ? 1 : 0) + (hasWalletAddresses ? userData?.bankdetails?.filter(item => item?.value)?.length || 0 : 0)}</span></span>
                        <span>ENCRYPTION: <span className="text-green-400">AES-256</span></span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs font-mono text-gray-400">
                        <span>v3.1.2</span>
                        <div className="w-1 h-1 rounded-full bg-gray-600"></div>
                        <span>INVESTMENT_TERMINAL</span>
                    </div>
                </div>
            </div>
        </div>
    </WrapUpP2P>
    );
};

export default PaymentMethod;